import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import { trackError } from '@cint/analytics';
import { NextAlert, Page, Spinner } from '@cint/wonderment';
import { ReactNode, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { OAUTH_CALLBACK_ROUTES } from '@/constants';

import {
  AUTH0_AUDIENCE,
  AUTH0_CALLBACK,
  AUTH0_CLIENT_ID,
  AUTH0_DOMAIN,
} from './env';

const DEFAULT_SCOPE = 'app:las openid email profile';

/**
 * Backup of the ad platforms' OAuth callback URL across the Auth0 login
 * redirect, which browsers blocking third-party cookies (Safari, incognito)
 * take on every page load. Wherever that login lands, a pending callback is
 * restored once authenticated — otherwise the one-time authorization code
 * would never be exchanged.
 */
const PENDING_OAUTH_CALLBACK_KEY = 'pendingOAuthCallback';
const PENDING_OAUTH_CALLBACK_MAX_AGE_MS = 15 * 60 * 1000;

const savePendingOAuthCallback = (url: string) => {
  localStorage.setItem(
    PENDING_OAUTH_CALLBACK_KEY,
    JSON.stringify({ url, savedAt: Date.now() })
  );
};

const clearPendingOAuthCallback = () => {
  localStorage.removeItem(PENDING_OAUTH_CALLBACK_KEY);
};

const peekPendingOAuthCallback = (): string | undefined => {
  const stored = localStorage.getItem(PENDING_OAUTH_CALLBACK_KEY);
  if (!stored) return undefined;

  try {
    const { url, savedAt } = JSON.parse(stored) ?? {};
    const age = Date.now() - savedAt;
    if (
      typeof url === 'string' &&
      typeof savedAt === 'number' &&
      age >= 0 &&
      age < PENDING_OAUTH_CALLBACK_MAX_AGE_MS
    )
      return url;
  } catch {
    // Unreadable entries are dropped below, like expired ones.
  }
  clearPendingOAuthCallback();
  return undefined;
};

const isOAuthCallbackPath = (pathname: string) =>
  OAUTH_CALLBACK_ROUTES.includes(pathname.replace(/\/+$/, ''));

interface AuthProviderProps {
  children: ReactNode;
  scopes?: string[] | string;
}

interface RequireAuthProps {
  children: ReactNode;
}

const fullPageStyle = {
  minHeight: '100vh',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
} as const;

const FullPageSpinner = () => (
  <Page style={fullPageStyle}>
    <Spinner size="xl" />
  </Page>
);

const FullPageError = ({
  title,
  description,
}: {
  title: string;
  description?: string;
}) => (
  <Page style={fullPageStyle}>
    <NextAlert variant="danger" title={title} description={description} />
  </Page>
);

const RequireAuth = ({ children }: RequireAuthProps) => {
  const { isAuthenticated, isLoading, error, loginWithRedirect } = useAuth0();
  const [redirectError, setRedirectError] = useState<Error | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const currentUrl = `${location.pathname}${location.search}`;
  const pendingOAuthCallback = isAuthenticated
    ? peekPendingOAuthCallback()
    : undefined;
  const isRestoringOAuthCallback =
    !!pendingOAuthCallback && pendingOAuthCallback !== currentUrl;

  useEffect(() => {
    if (!pendingOAuthCallback) return;
    clearPendingOAuthCallback();
    if (pendingOAuthCallback === currentUrl) return;

    trackError(
      'Auth0 login did not return to the OAuth callback; restored it',
      {
        landedOn: location.pathname,
        pendingOAuthCallbackPath: pendingOAuthCallback.split('?')[0],
      },
      true
    );
    navigate(pendingOAuthCallback, { replace: true });
  }, [pendingOAuthCallback, currentUrl, location.pathname, navigate]);

  useEffect(() => {
    // Bail on error too, otherwise a persistent auth error re-fires the redirect
    // in a loop instead of showing the error below.
    if (isLoading || isAuthenticated || error || redirectError) return;

    const returnTo = `${window.location.pathname}${window.location.search}`;
    if (isOAuthCallbackPath(window.location.pathname)) {
      savePendingOAuthCallback(returnTo);
    }

    loginWithRedirect({ appState: { returnTo } }).catch(setRedirectError);
  }, [isLoading, isAuthenticated, error, redirectError, loginWithRedirect]);

  if (error || redirectError) {
    return (
      <FullPageError
        title="Login failed"
        description={(error || redirectError)?.message}
      />
    );
  }

  // Rendering the app first would let the landing route (e.g. `/` → `/panels`)
  // navigate away before the callback is restored.
  if (!isAuthenticated || isRestoringOAuthCallback) return <FullPageSpinner />;

  return <>{children}</>;
};

export const AuthProvider = ({ children, scopes }: AuthProviderProps) => {
  const navigate = useNavigate();
  const appScopes = Array.isArray(scopes) ? scopes.join(' ') : scopes;
  const scope = [DEFAULT_SCOPE, appScopes].filter(Boolean).join(' ');

  if (!AUTH0_DOMAIN || !AUTH0_CLIENT_ID) {
    return (
      <FullPageError
        title="Login is misconfigured"
        description="Missing Auth0 domain or client ID."
      />
    );
  }

  return (
    <Auth0Provider
      domain={AUTH0_DOMAIN}
      clientId={AUTH0_CLIENT_ID}
      skipRedirectCallback={isOAuthCallbackPath(window.location.pathname)}
      // No refresh tokens: renew via iframe silent auth (authorization_code),
      // not the refresh-token grant, which errors in this tenant. Memory cache
      // means each reload fetches a fresh token, reflecting permission changes.
      authorizationParams={{
        audience: AUTH0_AUDIENCE,
        redirect_uri: AUTH0_CALLBACK,
        scope,
      }}
      onRedirectCallback={(appState) =>
        navigate(appState?.returnTo || window.location.pathname, {
          replace: true,
        })
      }
    >
      <RequireAuth>{children}</RequireAuth>
    </Auth0Provider>
  );
};
